import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogClose} from '@angular/material/dialog';
import {MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource} from '@angular/material/table';
import {Qewl} from '../../model/qewl';
import {Entity} from '../../entity/entity';
import {QewlService} from '../../services/qewl-service';
import {forkJoin, Observable} from 'rxjs';
import {QewlButton} from '../../model/qewl-button';
import {FileUtils} from "../../utils/file-utils";
import {MatTooltip} from '@angular/material/tooltip';
import {MatIcon} from '@angular/material/icon';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatButton} from '@angular/material/button';


@Component({
    selector: 'qewl-dialog',
    templateUrl: './qewl-dialog.component.html',
    styleUrls: ['./qewl-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatProgressSpinner, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatIcon, MatCellDef, MatCell, MatTooltip, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatDialogClose]
})

export class QewlDialogComponent implements OnInit {
  private readonly service = inject(QewlService);
  private readonly data = inject<Entity>(MAT_DIALOG_DATA);

  readonly allSelected = signal<boolean>(false);
  readonly isBusy = signal<boolean>(true);
  readonly errorMessage = signal<string>(undefined!);
  readonly successMessage = signal<string>(undefined!);
  entity: Entity;
  results: MatTableDataSource<Qewl> = new MatTableDataSource<Qewl>();
  displayedColumns: string[] = ['check-box', 'entity', 'line-amount', 'prn', 'quantity', 'complexity', 'controls'];
  readonly selectedLines = signal<Qewl[]>(undefined!);

  constructor
  () {
    this.entity = this.data;
  }

  ngOnInit(): void {
    this.getContent();
  }

  getContent() {
    this.isBusy.set(true);
    this.selectedLines.set([]);
    this.service.getQewlById(this.entity).subscribe({
      next: result => {
        this.results = new MatTableDataSource<Qewl>(result);
        if (this.results.data.length > 0) {
          this.results.data = this.checkIfPrnExistsOnEntity(result);
          this.results.data.forEach(q => this.setButtonAttributes(q));
          this.results.data.sort((a: Qewl, b: Qewl) => Number(b.button.enabled) - Number(a.button.enabled) ||
            Number(a.duplicateLine) - Number(b.duplicateLine) || a.lineNumber - b.lineNumber);
        }
      },
      error: error => {
        this.handleError(error)
      },
      complete: () => {
        this.isBusy.set(false)}
    })
  }

  selectAll() {
    this.allSelected.set(!this.allSelected());
    this.results.data.forEach(q => {
      this.selectLine(q)
    });
  }

  selectLine(line: Qewl): boolean {
    let boxShouldBeChecked = false;
    if (line.button.enabled && !(line.duplicateLine && line.duplicatePrn)) {
      if (this.selectedLines().includes(line)) {
        boxShouldBeChecked = false;
        line.selected = false;
        this.selectedLines.update(current => { const next = [...current]; next.splice(this.selectedLines().indexOf(line), 1); return next; });
      } else {
        boxShouldBeChecked = true;
        line.selected = true;
        this.selectedLines.update(current => [...current, line]);
      }
    }
    return boxShouldBeChecked;
  }

  submitSelected(): void {
    this.isBusy.set(true);
    const observables: Observable<void>[] = []
    for (let q of this.selectedLines()) {
      observables.push(this.service.sendToQewl(q))
    }
    forkJoin(observables).subscribe({
      next: () => this.successMessage.set("Success!"),
      error: (error) => this.handleError(error),
      complete: () => {
        this.getContent();
        this.selectedLines.set([]);
      }
    })
  }

  submitLine(line: Qewl) {
    if (!line.duplicateLine && !line.duplicatePrn && !line.duplicatePrnOnEntity) {
      this.sendToQewl(line)
    } else if (line.duplicateLine && line.duplicatePrn && !line.duplicatePrnOnEntity) {
      this.removeFromQewl(line);
    } else {
      this.errorMessage.set("Unable to submit line " + line.lineNumber);
    }
  }

  sendToQewl(qewl: Qewl): void {
    this.isBusy.set(true);
    this.service.sendToQewl(qewl).subscribe({
      next: () => this.successMessage.set("Success- line " + qewl.lineAmount + " was sent to QEWL."),
      error: (error) => this.handleError(error),
      complete: () => {
        this.getContent();
        this.selectedLines.set([]);
      }
    })
  }

  removeFromQewl(qewl: Qewl): void {
    this.isBusy.set(true);
    this.service.removeFromQewl(qewl).subscribe({
      next: () => {
        this.successMessage.set(qewl.entityId + " removed from QEWL.")},
      error: error => {
        this.handleError(error);
      },
      complete: () => {
        this.isBusy.set(false);
        this.allSelected.set(false);
        this.getContent();
      }
    });
  }

  setButtonAttributes(line: Qewl) {
    line.button = new QewlButton();
    if (line.previousMfgNum) {
      line.button.message = line.previousMfgNum;
      line.button.enabled = false;
    } else if (!line.duplicateLine && !line.duplicatePrn && !line.duplicatePrnOnEntity) {
      line.button.message = "Send to QEWL";
      line.button.enabled = true;
    } else if (line.duplicatePrnOnEntity) {
      let lineNumber = parseInt(this.results.data.find(q => q.prn == line.prn)!.lineNumber.toString()).toString();
      line.button.message = "Duplicate of line " + lineNumber;
      line.button.enabled = false;
    } else if (!line.duplicateLine && line.duplicatePrn) {
      line.button.message = "PRN exists in QEWL"
      line.button.enabled = false;
    } else if (line.duplicateLine && line.duplicatePrn && !line.duplicatePrnOnEntity) {
      line.button.message = "Remove from QEWL"
      line.button.enabled = true;
    } else {
      line.button.message = "ERROR";
      line.button.enabled = false;
    }
  }

  checkIfPrnExistsOnEntity(lines: Qewl[]): Qewl[] {
    let prnList: string[] = [];
    lines.forEach(l => {
      l.duplicatePrnOnEntity = true;
      if (!prnList.includes(l.prn)) {
        prnList.push(l.prn);
        l.duplicatePrnOnEntity = false;
      }
    });
    return lines;
  }

  disableCheckbox(line: Qewl): boolean {
    let checkboxDisabled = true;
    if (line.button.enabled && !(line.duplicateLine && line.duplicatePrn)) {
      checkboxDisabled = false;
    }
    return checkboxDisabled;
  }

  formatFloat(number: any, decimalPlaces: any): string {
    return parseFloat(number).toFixed(decimalPlaces);
  }

  getMfgNumberExport() {
    this.isBusy.set(true);
    this.service.getMfgNumberExport(this.entity).subscribe({
      next: (result: any) => {
        FileUtils.downloadFile(result, this.entity.id.entityId + ";" + this.entity.id.entityRevision + "_MfgNumberExport.xlsx");
        this.isBusy.set(false);
      },
      error: (error) => this.handleError(error),
      complete: () => {
        this.getContent();
        this.selectedLines.set([]);
      }
    })
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.allSelected.set(false);
    this.selectedLines.set([]);
    this.errorMessage.set(error.message);
  }

}

